import { z } from 'zod';
import { CalendarDate, TaskPriority } from './project.js';
/**
 * Nút "Auto Assign" của form việc — APM-AIA-c.
 *
 * Người dùng chốt 2026-09-25: **AI chỉ chọn người và giải thích; code tính giờ
 * và ngày** cho người được chọn (`điểm × giờ/điểm của họ`, rồi xếp vào lịch
 * trống sớm nhất). Không có key AI thì xếp hạng tất định — vẫn điền đủ ô.
 */
export const AutoAssignInput = z.object({
    title: z.string().trim().min(3).max(500),
    description: z.string().max(20_000).optional(),
    checklist: z.array(z.string().max(500)).max(100).optional(),
    taskTypeId: z.uuid().nullable().optional(),
    priority: TaskPriority.optional(),
    /** Khối lượng việc — thứ quy ra giờ. Không có điểm thì không có giờ để xếp lịch. */
    storyPoints: z.number().int().positive().max(1_000),
    /**
     * Ô "Thời gian làm (giờ)" người dùng đã điền — APM-AIA-d (người dùng chốt
     * 2026-09-28). **Chỉ** dùng khi dự án chưa có tốc độ theo story point: khi ấy
     * đây là con số duy nhất có thật để xếp lịch ra hạn chót. Có tốc độ thì giờ
     * vẫn là `điểm × giờ/điểm` của người được chọn, như APM-AIA-b.
     */
    estimateHours: z.number().positive().max(10_000).optional(),
    runId: z.uuid().optional(),
});
/** Kết quả model nộp — chỉ **ai** và **vì sao**. */
export const AutoAssignSuggestion = z.object({
    userId: z.uuid(),
    /** Một hai câu nêu căn cứ: kỹ năng nào khớp, còn bao nhiêu giờ trống, đang gánh gì. */
    reason: z.string().min(1).max(500),
    alternatives: z
        .array(z.object({ userId: z.uuid(), reason: z.string().min(1).max(300) }))
        .max(3),
});
export const AutoAssignPace = z.object({
    hoursPerPoint: z.number().positive(),
    /** `person` — của chính người ấy trong dự án này; `project` — trung vị cả dự án. */
    basis: z.enum(['person', 'project']),
    sampleSize: z.number().int().positive(),
});
export const AutoAssignResult = z.object({
    /** `null` khi dự án không có ai để giao (không thành viên nào đang làm việc). */
    assigneeId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    reason: z.string(),
    /**
     * `null` khi chưa có tốc độ nào để quy điểm ra giờ **và** người dùng chưa điền
     * giờ — form để trống ô giờ. `pace: null` mà có số ⇒ đó là giờ người dùng điền.
     */
    estimateHours: z.number().positive().nullable(),
    startDate: CalendarDate.nullable(),
    /** `null` khi không có giờ, hoặc lịch 90 ngày tới không đủ chỗ. */
    dueDate: CalendarDate.nullable(),
    pace: AutoAssignPace.nullable(),
    /** `ai` — model chọn người; `ranked` — xếp hạng tất định (xong sớm nhất). */
    source: z.enum(['ai', 'ranked']),
    aiSkipped: z.enum(['no_key', 'failed']).nullable(),
});
//# sourceMappingURL=auto-assign.js.map